import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  ArrowLeft,
  Download,
  FileJson,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Building2,
  User,
  CreditCard,
  Banknote,
  Hash,
  FileCode,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Percent,
  Shield,
} from "lucide-react";
import {
  multiAmcGetWorkOrder,
  multiAmcDeleteWorkOrder,
  multiAmcGetWorkOrderFileUrl,
  multiAmcGetWorkOrderPreviewUrl,
  multiAmcGetWorkOrderPages,
} from "../api";
import "./MultiAMCWorkOrderDetail.css";

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true, icon: Icon, fieldCount }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="multi-amc-collapsible-section">
      <button className="multi-amc-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        {Icon && <Icon size={18} className="section-icon" />}
        <span>{title}</span>
        {fieldCount !== undefined && (
          <span className="field-count">{fieldCount} fields</span>
        )}
      </button>
      {isOpen && <div className="multi-amc-section-content">{children}</div>}
    </div>
  );
};

// Field Display Component
const FieldDisplay = ({ label, value, type = "text", onCopy, copiedField, fieldKey }) => {
  const displayValue = value === null || value === undefined ? "-" : String(value);
  const hasValue = value !== null && value !== undefined && value !== "";

  const getTypeClass = () => {
    if (!hasValue) return "empty";
    if (type === "choice") return "choice";
    if (type === "date") return "date";
    if (type === "number") return "number";
    return "";
  };

  return (
    <div className={`multi-amc-field ${getTypeClass()}`}>
      <span className="multi-amc-field-label">{label}</span>
      <span className={`multi-amc-field-value ${type === "number" ? "monospace" : ""}`}>
        {displayValue}
      </span>
      {hasValue && (
        <button
          className="multi-amc-copy-btn"
          onClick={() => onCopy(displayValue, fieldKey)}
          title="Copy"
        >
          <Copy size={12} />
          {copiedField === fieldKey && <span className="copied-tooltip">Copied!</span>}
        </button>
      )}
    </div>
  );
};

// Section Icon Map
const getSectionIcon = (sectionName) => {
  const iconMap = {
    distributor_info: Building2,
    investor_category: User,
    kyc_details: Shield,
    investor_details: User,
    contact_details: MapPin,
    bank_details: Banknote,
    lumpsum_investment: CreditCard,
    payment_details: CreditCard,
    nominee_details: User,
    signature_details: FileText,
    sip_details: Calendar,
    otm_details: Banknote,
    fatca_crs_details: FileCode,
    additional_kyc: FileText,
    folio_investor_details: Hash,
  };
  return iconMap[sectionName] || FileText;
};

// Format section name for display
const formatSectionName = (name) => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Recursive JSON Renderer
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="multi-amc-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="multi-amc-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="multi-amc-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="multi-amc-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="multi-amc-json-empty">[]</span>;
    return (
      <div className="multi-amc-json-array">
        {data.map((item, index) => (
          <div key={index} className="multi-amc-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="multi-amc-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="multi-amc-json-empty">{"{}"}</span>;
    return (
      <div className="multi-amc-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="multi-amc-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="multi-amc-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

// Multi-Page Document Viewer Component
const MultiPageDocumentViewer = ({ workOrderId, totalPages, filename, onError }) => {
  const [loadedPages, setLoadedPages] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [allFailed, setAllFailed] = useState(false);

  const getPageUrl = (pageNum) => {
    // Use the API function which includes token and proper URL construction
    return multiAmcGetWorkOrderPreviewUrl(workOrderId, pageNum);
  };

  const handlePageLoad = (pageNum) => {
    setLoadedPages(prev => ({ ...prev, [pageNum]: true }));
  };

  const handlePageError = (pageNum) => {
    setLoadErrors(prev => {
      const newErrors = { ...prev, [pageNum]: true };
      if (pageNum === 0 && onError) {
        setAllFailed(true);
        onError();
      }
      return newErrors;
    });
  };

  const pageCount = totalPages || 1;

  if (allFailed) {
    return null;
  }

  return (
    <div className="multi-amc-document-scroll-viewer">
      {Array.from({ length: pageCount }, (_, i) => (
        <div key={i} className="multi-amc-document-page">
          {loadErrors[i] ? (
            <div className="multi-amc-page-error">
              <AlertCircle size={20} />
              <span>Page {i + 1} failed to load</span>
            </div>
          ) : (
            <img
              src={getPageUrl(i)}
              alt={`${filename || 'Document'} - Page ${i + 1}`}
              className="multi-amc-page-image"
              onLoad={() => handlePageLoad(i)}
              onError={() => handlePageError(i)}
              loading={i === 0 ? "eager" : "lazy"}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// Extraction Results Viewer Component
const ExtractionResultsViewer = ({ extractedData, onCopy, copiedField }) => {
  if (!extractedData || Object.keys(extractedData).length === 0) {
    return (
      <div className="multi-amc-no-data">
        <FileText size={48} />
        <p>No extraction data available</p>
        <span>Extracted fields will appear here once processing completes</span>
      </div>
    );
  }

  // Group fields by section
  const sections = {};
  
  // Check if data is already grouped by sections
  const firstKey = Object.keys(extractedData)[0];
  const isGrouped = typeof extractedData[firstKey] === 'object' && !Array.isArray(extractedData[firstKey]);

  if (isGrouped) {
    Object.entries(extractedData).forEach(([sectionName, sectionData]) => {
      if (sectionData && typeof sectionData === 'object' && !sectionName.startsWith('_')) {
        sections[sectionName] = sectionData;
      }
    });
  } else {
    // Flat structure - put everything in one section
    sections['extracted_fields'] = extractedData;
  }

  const countFields = (data) => {
    if (!data || typeof data !== 'object') return 0;
    return Object.entries(data).filter(([k, v]) => !k.startsWith('_') && v !== null && v !== undefined).length;
  };

  return (
    <div className="multi-amc-extraction-results">
      {Object.entries(sections).map(([sectionName, sectionData]) => {
        const SectionIcon = getSectionIcon(sectionName);
        const fields = Object.entries(sectionData || {}).filter(([k]) => !k.startsWith('_'));
        
        return (
          <CollapsibleSection
            key={sectionName}
            title={formatSectionName(sectionName)}
            icon={SectionIcon}
            fieldCount={countFields(sectionData)}
            defaultOpen={sectionName === 'distributor_info' || sectionName === 'investor_details' || sectionName === 'folio_investor_details'}
          >
            <div className="multi-amc-fields-grid">
              {fields.map(([fieldName, fieldValue]) => (
                <FieldDisplay
                  key={fieldName}
                  label={fieldName.replace(/_/g, ' ')}
                  value={fieldValue}
                  onCopy={onCopy}
                  copiedField={copiedField}
                  fieldKey={`${sectionName}-${fieldName}`}
                />
              ))}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
};

const MultiAMCWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("extracted");
  const [copiedField, setCopiedField] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(true);
  const [fileLoadError, setFileLoadError] = useState(false);

  const fileUrl = workOrderId ? multiAmcGetWorkOrderFileUrl(workOrderId) : null;
  const previewUrl = workOrderId ? multiAmcGetWorkOrderPreviewUrl(workOrderId) : null;

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
    }
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await multiAmcGetWorkOrder(workOrderId);
      // API returns { success: true, data: {...} }
      if (response.success && response.data) {
        setWorkOrder(response.data);
      } else {
        setError(response.error || "Failed to load work order");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this work order? This cannot be undone.")) {
      return;
    }

    try {
      await multiAmcDeleteWorkOrder(workOrderId);
      navigate("/multi-amc/work-orders");
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleDownloadJSON = () => {
    if (!workOrder) return;

    const data = {
      extracted_data: workOrder.extracted_data || {},
      metadata: {
        work_order_id: workOrder.id,
        filename: workOrder.filename,
        amc_code: workOrder.amc_code,
        amc_name: workOrder.amc_name,
        form_type: workOrder.form_type,
        total_pages: workOrder.total_pages,
        processing_time: workOrder.processing_time,
        created_at: workOrder.created_at,
        status: workOrder.status,
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}_extraction.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!workOrder?.extracted_data) return;

    // Flatten nested data for CSV
    const flatData = {};
    const extractedData = workOrder.extracted_data;

    const flatten = (obj, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}_${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          flatten(value, newKey);
        } else {
          flatData[newKey] = value;
        }
      });
    };

    flatten(extractedData);

    const headers = Object.keys(flatData);
    const values = Object.values(flatData).map(v => 
      `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`
    );

    const csv = [headers.join(','), values.join(',')].join('\n');

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}_extraction.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={20} className="status-icon completed" />;
      case "failed":
        return <XCircle size={20} className="status-icon failed" />;
      case "processing":
        return <RefreshCw size={20} className="status-icon processing spin" />;
      default:
        return <AlertCircle size={20} className="status-icon pending" />;
    }
  };

  const getFileExtension = (filename) => {
    if (!filename) return 'pdf';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'pdf';
  };

  const isImageFile = (filename) => {
    const ext = getFileExtension(filename);
    // TIFF files are converted to PDF on backend, so use PDF viewer for them
    return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
  };

  if (loading) {
    return (
      <div className="multi-amc-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error && !workOrder) {
    return (
      <div className="multi-amc-detail-error">
        <XCircle size={48} />
        <h3>Error Loading Work Order</h3>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="multi-amc-detail-error">
        <AlertCircle size={48} />
        <h3>Work Order Not Found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const extractedData = workOrder.extracted_data || {};

  return (
    <div className="multi-amc-work-order-detail">
      {/* Back Button */}
      <button className="multi-amc-back-button" onClick={() => navigate("/multi-amc/work-orders")}>
        <ArrowLeft size={18} />
        Back to Work Orders
      </button>

      {/* Header */}
      <div className="multi-amc-detail-header">
        <div className="multi-amc-detail-header-left">
          <div className="multi-amc-detail-title">
            <h1>
              <FileText size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span className="multi-amc-pipeline-tag">Multi-AMC</span>
            {workOrder.amc_name && (
              <span className="multi-amc-amc-tag">{workOrder.amc_name}</span>
            )}
            {workOrder.form_type && (
              <span className="multi-amc-form-tag">{workOrder.form_type.replace(/_/g, ' ')}</span>
            )}
          </div>
          <div className="multi-amc-detail-meta">
            <span className="multi-amc-meta-item">
              <strong>ID:</strong> {workOrder.id}
              <button
                className="multi-amc-copy-btn"
                onClick={() => copyToClipboard(workOrder.id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.barcode && (
              <span className="multi-amc-meta-item">
                <strong>Barcode:</strong> {workOrder.barcode}
              </span>
            )}
          </div>
        </div>

        <div className="multi-amc-detail-header-right">
          <div className={`multi-amc-status-badge status-${workOrder.status}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {error && (
        <div className="multi-amc-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Info Cards */}
      <div className="multi-amc-info-cards">
        <div className="multi-amc-info-card">
          <Clock size={20} />
          <div>
            <span className="multi-amc-info-label">Created</span>
            <span className="multi-amc-info-value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
        <div className="multi-amc-info-card">
          <FileText size={20} />
          <div>
            <span className="multi-amc-info-label">Pages</span>
            <span className="multi-amc-info-value">{workOrder.total_pages || "-"}</span>
          </div>
        </div>
        <div className="multi-amc-info-card highlight">
          <Building2 size={20} />
          <div>
            <span className="multi-amc-info-label">AMC</span>
            <span className="multi-amc-info-value">{workOrder.amc_code?.toUpperCase() || "-"}</span>
          </div>
        </div>
        <div className="multi-amc-info-card">
          <RefreshCw size={20} />
          <div>
            <span className="multi-amc-info-label">Processing Time</span>
            <span className="multi-amc-info-value">
              {workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(2)}s` : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="multi-amc-action-bar">
        <button
          className={`multi-amc-action-btn toggle ${showFileViewer ? 'active' : ''}`}
          onClick={() => setShowFileViewer(!showFileViewer)}
          title={showFileViewer ? "Hide File Viewer" : "Show File Viewer"}
        >
          {showFileViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          {showFileViewer ? "Hide File" : "Show File"}
        </button>
        <button className="multi-amc-action-btn primary" onClick={handleDownloadJSON}>
          <FileJson size={16} />
          JSON
        </button>
        <button className="multi-amc-action-btn secondary" onClick={handleDownloadCSV} disabled={!Object.keys(extractedData).length}>
          <FileSpreadsheet size={16} />
          CSV
        </button>
        <button className="multi-amc-action-btn refresh" onClick={fetchWorkOrder}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="multi-amc-action-btn danger" onClick={handleDelete}>
          <Trash2 size={16} />
          Delete
        </button>
      </div>

      {/* Split View Container */}
      <div className={`multi-amc-split-container ${showFileViewer ? 'with-file' : 'no-file'}`}>
        {/* File Panel */}
        {showFileViewer && (
          <div className="multi-amc-file-panel">
            <div className="multi-amc-file-header">
              <h3>
                <FileText size={18} />
                Document Preview
              </h3>
              <div className="multi-amc-file-controls">
                <span className="multi-amc-file-type-badge">
                  {getFileExtension(workOrder.filename).toUpperCase()}
                </span>
                <a
                  href={fileUrl}
                  download
                  className="multi-amc-file-control-btn"
                  title="Download File"
                >
                  <Download size={16} />
                </a>
              </div>
            </div>
            <div className="multi-amc-file-viewer">
              {fileLoadError ? (
                <div className="multi-amc-file-unavailable">
                  <FileText size={48} />
                  <p>Preview not available</p>
                  <a href={fileUrl} download className="multi-amc-download-link">
                    <Download size={16} />
                    Download File
                  </a>
                </div>
              ) : isImageFile(workOrder.filename) ? (
                <MultiPageDocumentViewer
                  workOrderId={workOrderId}
                  totalPages={workOrder.total_pages}
                  filename={workOrder.filename}
                  onError={() => setFileLoadError(true)}
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title="Document Preview"
                  className="multi-amc-file-iframe"
                  onError={() => setFileLoadError(true)}
                />
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="multi-amc-data-panel">
          <div className="multi-amc-tabs">
            <button
              className={`multi-amc-tab ${activeTab === "extracted" ? "active" : ""}`}
              onClick={() => setActiveTab("extracted")}
            >
              <FileText size={16} />
              Extracted Data
            </button>
            <button
              className={`multi-amc-tab ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              <FileCode size={16} />
              Raw JSON
            </button>
            <button
              className={`multi-amc-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              <Hash size={16} />
              Metadata
            </button>
          </div>

          <div className="multi-amc-tab-content">
            {activeTab === "extracted" && (
              <ExtractionResultsViewer
                extractedData={extractedData}
                onCopy={copyToClipboard}
                copiedField={copiedField}
              />
            )}

            {activeTab === "raw" && (
              <div className="multi-amc-raw-json">
                <div className="multi-amc-raw-json-header">
                  <button onClick={() => copyToClipboard(JSON.stringify(extractedData, null, 2), "raw-json")}>
                    <Copy size={14} />
                    Copy JSON
                    {copiedField === "raw-json" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <div className="multi-amc-json-viewer">
                  <JsonRenderer data={extractedData} />
                </div>
              </div>
            )}

            {activeTab === "metadata" && (
              <div className="multi-amc-metadata">
                <table className="multi-amc-metadata-table">
                  <tbody>
                    <tr className="multi-amc-metadata-section">
                      <td colSpan="2">Work Order Information</td>
                    </tr>
                    <tr>
                      <td>Work Order ID</td>
                      <td>{workOrder.id}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>{workOrder.status}</td>
                    </tr>
                    <tr>
                      <td>Filename</td>
                      <td>{workOrder.filename || "-"}</td>
                    </tr>
                    <tr>
                      <td>Total Pages</td>
                      <td>{workOrder.total_pages || "-"}</td>
                    </tr>
                    {workOrder.barcode && (
                      <tr>
                        <td>Barcode</td>
                        <td>{workOrder.barcode}</td>
                      </tr>
                    )}
                    
                    <tr className="multi-amc-metadata-section">
                      <td colSpan="2">AMC & Form Information</td>
                    </tr>
                    <tr>
                      <td>AMC Code</td>
                      <td>{workOrder.amc_code || "-"}</td>
                    </tr>
                    <tr>
                      <td>AMC Name</td>
                      <td>{workOrder.amc_name || "-"}</td>
                    </tr>
                    <tr>
                      <td>Form Type</td>
                      <td>{workOrder.form_type?.replace(/_/g, ' ') || "-"}</td>
                    </tr>
                    <tr>
                      <td>Detected Form Type</td>
                      <td>{workOrder.detected_form_type?.replace(/_/g, ' ') || "-"}</td>
                    </tr>
                    
                    <tr className="multi-amc-metadata-section">
                      <td colSpan="2">Processing Information</td>
                    </tr>
                    <tr>
                      <td>Created At</td>
                      <td>{formatDate(workOrder.created_at)}</td>
                    </tr>
                    <tr>
                      <td>Updated At</td>
                      <td>{formatDate(workOrder.updated_at)}</td>
                    </tr>
                    <tr>
                      <td>Processing Time</td>
                      <td>{workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(2)}s` : "-"}</td>
                    </tr>
                    <tr>
                      <td>Detection Time</td>
                      <td>{workOrder.detection_time ? `${parseFloat(workOrder.detection_time).toFixed(2)}s` : "-"}</td>
                    </tr>
                    
                    {workOrder.error_message && (
                      <>
                        <tr className="multi-amc-metadata-section">
                          <td colSpan="2">Error Information</td>
                        </tr>
                        <tr className="error-row">
                          <td>Error Message</td>
                          <td>{workOrder.error_message}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiAMCWorkOrderDetail;
